import { FileGrid, FileTile, fileMeta } from '@codezo/quote-detail';
import { useState } from 'react';
import { FilePreview, type FilePreviewItem } from '../../../../components/ui/file-preview';
import type { ClientItemAttachment } from '../components/QuoteOffersTab';

/**
 * The files on one product, under the product.
 *
 * Read-only, and only ever a list: a file belongs to the quote line and is
 * uploaded, described and deleted on the admin's copy of this row — every offer
 * for the line serves this same list, so a client removing one here would take
 * it off offers already sent. See `QuoteItemFiles` in the admin app.
 *
 * Where the files come from is the awkward part, and it is a backend rule rather
 * than a choice: `OrderQuoteItem.attachments` is admin-only and hands a client an
 * empty list, so the only copy this side can read rides on the offers, and only
 * for batches already sent. That is why the list arrives as a prop from the page
 * — which collects it out of the offers tab — rather than off the item beside it.
 *
 * Empty is a state, not an absence: the section stays, saying there are no files,
 * the way the admin's copy does. Rendering nothing left no way to tell "this
 * product has no files" from "this panel isn't working" — which is exactly the
 * question it got asked. What it cannot say is WHY it is empty: a line whose
 * files are held back until a batch is sent looks the same from here, and that
 * distinction is the admin's to see, not the client's.
 */
export function ItemAttachments({ files }: { files: ClientItemAttachment[] }) {
	const [previewIndex, setPreviewIndex] = useState<number | null>(null);

	/* Only files whose URL has resolved can be opened; the rest still get a row,
	   so nothing silently goes missing from the count. */
	const previewFiles: FilePreviewItem[] = files
		.filter((f) => f.url)
		.map((f) => ({
			id: f.id,
			url: f.url as string,
			name: f.name,
			mimeType: f.mimeType,
			size: f.size,
		}));

	return (
		<div className="flex flex-col gap-2">
			{/* Empty is a state, not an absence: the section stays, saying there are
			    no files, rather than leaving the foot of the card blank. It says it
			    without a heading, though — "Files 0" over "no files" is the same
			    sentence twice. */}
			{files.length === 0 ? (
				<span className="text-sm text-text-4">No files on this product.</span>
			) : (
				<span className="text-xs font-medium tracking-[0.14em] text-text-3 uppercase">
					Files
					<span className="ml-2 font-mono text-xs font-normal tracking-normal text-text-4 normal-case">
						{files.length}
					</span>
				</span>
			)}

			{files.length > 0 && (
				<FileGrid>
					{files.map((file) => (
						<FileTile
							key={file.id}
							name={file.name}
							meta={fileMeta(file.name, file.mimeType, file.size)}
							description={file.description}
							/* Only a real image goes in an <img>: handing one the raw url
							   of a PDF renders a broken-image glyph. */
							preview={
								file.mimeType.startsWith('image/') && file.url ? (
									<img
										src={file.url}
										alt=""
										className="h-full w-full object-cover"
									/>
								) : undefined
							}
							onOpen={
								file.url
									? () => {
											const idx = previewFiles.findIndex(
												(f) => f.id === file.id
											);
											if (idx >= 0) setPreviewIndex(idx);
										}
									: undefined
							}
						/>
					))}
				</FileGrid>
			)}

			{previewIndex !== null && (
				<FilePreview
					files={previewFiles}
					initialIndex={previewIndex}
					onClose={() => setPreviewIndex(null)}
				/>
			)}
		</div>
	);
}
